import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Checkbox, Input, LoaderCircle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CheckIcon from 'mdi-react/CheckIcon'
import CloseIcon from 'mdi-react/CloseIcon'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'

import type { NumberRecordType } from '../types'

import queries from '../../queries'
import adminResources from '../../resources'

type Props = {
  onClose: () => void
  selectedNumbers: Map<string, NumberRecordType>
  isOpen: boolean
}

export const GroupForm: FC<Props> = ({ onClose, isOpen, selectedNumbers }) => {
  const [showInput, setShowInput] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null)

  const queryClient = useQueryClient()

  const {
    data: groups,
    isLoading,
    isError,
  } = useQuery(queries.adminGroupsList(), adminResources.getPhoneGroupsList)

  const createGroupMutation = useMutation(adminResources.createPhoneGroup, {
    onSuccess(group) {
      queryClient.setQueryData(queries.adminGroupsList(), [group, ...groups!])
      setShowInput(false)
      setInputValue('')
    },
  })

  const addSelectedNumbersToPhoneGroupMutation = useMutation(
    adminResources.addNumbersToPhoneGroup,
    {
      onSuccess() {
        queryClient.invalidateQueries(queries.adminActiveNumbersList())
        onClose()
      },
    }
  )

  useEffect(() => {
    setSelectedGroup(null)
    setShowInput(false)
    setInputValue('')
  }, [isOpen])

  const onCreateHandler = () => {
    if (inputValue) {
      createGroupMutation.mutate({ name: inputValue })
    }
  }

  const onAddNumbersToPhoneGroupHandler = () => {
    if (selectedGroup) {
      // We spread the map here because datatype Map does not have a .map function like Arrays
      const numbers: Omit<NumberRecordType, 'id'>[] = [...selectedNumbers].map(([, item]) => ({
        ...item,
      }))

      const payload = {
        groupId: selectedGroup,
        numbers,
      }

      addSelectedNumbersToPhoneGroupMutation.mutate(payload)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-[60px]">
        <LoaderCircle />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="min-h-[60px]">
        <NoResults />
      </div>
    )
  }

  return (
    <div className="flex h-[30rem] flex-col gap-2 pt-2 md:pt-5 lg:h-[35rem]">
      <div className="flex items-center justify-between">
        <h2 className="mb-0">
          <Translation id="Groups" />
        </h2>
        <Button onClick={() => setShowInput(!showInput)}>
          <Translation id="Add new group" />
        </Button>
      </div>

      <div className="grow overflow-y-auto">
        {groups.length === 0 && <NoResults />}
        {groups.map(group => (
          <div
            className="flex w-full cursor-pointer justify-between rounded p-2 hover:bg-gray-50"
            key={group.id}
            onClick={() => setSelectedGroup(group.id)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                setSelectedGroup(group.id)
              }
            }}
          >
            <span
              onClick={() => setSelectedGroup(group.id)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setSelectedGroup(group.id)
                }
              }}
            >
              {group.name}
            </span>
            <Checkbox
              checked={group.id === selectedGroup}
              id={group.id.toString()}
              className="show-all-companies-input"
              marginTop={0}
              marginLeft={0}
              onChange={() => setSelectedGroup(group.id)}
            />
          </div>
        ))}
      </div>
      <div className={`relative p-2 ${showInput ? 'block' : 'hidden'}`}>
        <Input
          className="h-10 w-full pr-[5rem]"
          id="Test"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
        />
        <div className="absolute right-4 top-4 flex gap-2">
          <CheckIcon onClick={onCreateHandler} />
          <CloseIcon onClick={() => setShowInput(false)} />
        </div>
      </div>
      <div className="flex flex-col">
        <Button
          onClick={onAddNumbersToPhoneGroupHandler}
          variant="primary"
          isLoading={addSelectedNumbersToPhoneGroupMutation.isLoading}
        >
          <Translation id="Add to group" />
        </Button>
        <Button onClick={onClose} disabled={addSelectedNumbersToPhoneGroupMutation.isLoading}>
          <Translation id="Cancel" />
        </Button>
      </div>
    </div>
  )
}
